import { NexoyaPortfolioParentContentsSortField, NexoyaSortOrder } from '../../../types';
import { extractIdsByKey } from '../../../utils/array';
import { ContentFilterState } from '../../../store/content-filter';
import { toPortfolioContentLabelFilters, type ContentLabelSelection } from '../../../store/contentLabelFilterSelection';

export const humanizeSortField = (sortField: NexoyaPortfolioParentContentsSortField) => {
  switch (sortField) {
    case NexoyaPortfolioParentContentsSortField.ContentTitle:
      return 'Content title';
    case NexoyaPortfolioParentContentsSortField.ContentType:
      return 'Content type';
    case NexoyaPortfolioParentContentsSortField.LabelName:
      return 'Label name';
    case NexoyaPortfolioParentContentsSortField.ImpactGroupName:
      return 'Impact group name';
    case NexoyaPortfolioParentContentsSortField.ProviderId:
      return 'Channel';
    case NexoyaPortfolioParentContentsSortField.IsIncludedInOptimization:
      return 'Optimization status';
    case NexoyaPortfolioParentContentsSortField.ContentRuleName:
      return 'Content rule name';
    case NexoyaPortfolioParentContentsSortField.ImpactGroupRuleName:
      return 'Impact group rule name';
    case NexoyaPortfolioParentContentsSortField.AttributionRuleName:
      return 'Attribution rule name';
    case NexoyaPortfolioParentContentsSortField.IsRuleManaged:
      return 'Content mode';
    default:
      return '';
  }
};

// The default for filter states built without the label slice; it reads as "nothing selected".
const NO_CONTENT_LABELS: ContentLabelSelection = new Map();

type BuildOptions = { legacyLabelsEnabled?: boolean };

export const buildPortfolioContentFilterVariables = (
  teamId: number,
  portfolioId: number,
  filters: ContentFilterState,
  { legacyLabelsEnabled = true }: BuildOptions = {},
) => {
  const {
    providersFilter = [],
    adAccountsFilter = [],
    labelsFilter = [],
    impactGroupsFilter = [],
    contentRulesFilter = [],
    attributionRulesFilter = [],
    impactGroupRulesFilter = [],
    contentLabels = NO_CONTENT_LABELS,
    isIncludedInOptimization,
    isRuleManaged,
    titleContains,
    includeNoLabel,
    sortOrder,
    pageSize,
  } = filters;

  // Hidden portfolio labels must not filter or sort rows by something the user can no longer see or clear.
  const sortField =
    !legacyLabelsEnabled && filters.sortField === NexoyaPortfolioParentContentsSortField.LabelName
      ? undefined
      : filters.sortField;

  // A whole-channel selection puts its provider in providersFilter AND its accounts in
  // adAccountsFilter; only send providerIds not already covered by adAccountIds.
  const adAccountProviderIds = new Set(adAccountsFilter.map((a) => a.providerId));
  const filteredProviderIds = extractIdsByKey(
    providersFilter.filter((p) => !adAccountProviderIds.has(p.provider_id)),
    'provider_id',
  );
  const filteredAdAccountIds = extractIdsByKey(adAccountsFilter, 'contentId');
  const filteredLabelIds = legacyLabelsEnabled ? extractIdsByKey(labelsFilter, 'labelId') : [];
  const filteredImpactGroupIds = extractIdsByKey(impactGroupsFilter, 'impactGroupId');
  const filteredContentRuleIds = extractIdsByKey(contentRulesFilter, 'contentRuleId');
  const filteredAttributionRuleIds = extractIdsByKey(attributionRulesFilter, 'attributionRuleId');
  const filteredImpactGroupRuleIds = extractIdsByKey(impactGroupRulesFilter, 'impactGroupRuleId');
  // Canonical (sorted) and only sent when non-empty: Apollo keys the cache on array order, and an
  // omitted field, null and [] are three different keys.
  const contentLabelFilters = toPortfolioContentLabelFilters(contentLabels);

  return {
    teamId,
    portfolioId,
    sortOrder: sortField ? sortOrder || NexoyaSortOrder.Asc : NexoyaSortOrder.Asc,
    sortField: sortField || NexoyaPortfolioParentContentsSortField.ProviderId,
    first: pageSize,
    portfolioContentFilter: {
      ...(filteredAttributionRuleIds.length ? { attributionRuleIds: filteredAttributionRuleIds } : {}),
      ...(filteredAdAccountIds.length ? { adAccountIds: filteredAdAccountIds } : {}),
      ...(contentLabelFilters.length ? { contentLabelFilters } : {}),
      ...(filteredContentRuleIds.length ? { contentRuleIds: filteredContentRuleIds } : {}),
      ...(filteredImpactGroupIds.length ? { impactGroupIds: filteredImpactGroupIds } : {}),
      ...(filteredImpactGroupRuleIds.length ? { impactGroupRuleIds: filteredImpactGroupRuleIds } : {}),
      ...(filteredLabelIds.length ? { labelIds: filteredLabelIds } : {}),
      ...(legacyLabelsEnabled && includeNoLabel ? { includeNoLabel: true } : {}),
      ...(filteredProviderIds.length ? { providerIds: filteredProviderIds } : {}),
      ...(titleContains ? { titleContains } : {}),
      ...(isRuleManaged !== undefined ? { isRuleManaged } : {}),
      ...(isIncludedInOptimization !== undefined ? { isIncludedInOptimization } : {}),
    },
  };
};
